"use client";

import { router } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDataTable } from "@/hooks/use-data-table";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import { usersPath } from "@/routes";
import type { PagyMetadata, RansackFilters, User } from "@/types";

import { columns } from "./columns";

interface UsersDataTableProps {
  users: User[];
  pagy: PagyMetadata;
  filters: RansackFilters;
}

export function UsersDataTable({
  users,
  pagy,
  filters,
}: UsersDataTableProps) {
  const filterKey = "full_name_or_email_cont" as const;
  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({ filters, pagy, path: usersPath() });

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue(filters[filterKey] ?? "");

  useEffect(() => {
    if ((filters[filterKey] ?? "") !== debouncedSearchValue) {
      handleFilterChange({ [filterKey]: debouncedSearchValue || undefined });
    }
    // Depend only on debounced value to avoid loops when filters change from the server.
  }, [debouncedSearchValue]); // intentionally omit handleFilterChange to avoid effect loops

  const clearFilters = () => {
    setSearchValue("");
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Input
          placeholder="Buscar por email o nombre..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className="max-w-sm"
        />
        {searchValue && (
          <Button variant="outline" onClick={clearFilters}>
            <Trash2 className="h-4 w-4" /> Limpiar filtros
          </Button>
        )}
      </div>

      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={users}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
        />
      </div>

      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="usuarios"
      />
    </div>
  );
}
